// Sort model for the Student Inquiries table.
//
// Twin of Scheduling/slotColumns.js — same blank-sinking semantics, same
// three-line `nextSort`. Deliberately duplicated rather than shared: promoting
// the generic half into a util means editing the Slot Editor, and two call
// sites is not yet a pattern. If a THIRD table wants sortable headers, promote
// `nextSort` + the comparator then, and collapse both copies into it.

import { interestList } from '../../utils/inquiryService';

// Firestore Timestamp | {seconds} | {_seconds} → epoch ms. Shared with
// Inquiries.js (which needs it to render the Submitted column) so the page and
// its sort agree on what a date IS — two copies could disagree on the
// `_seconds` shape and silently order the table differently than it reads.
export function tsToMs(ts) {
	if (!ts) return 0;
	if (typeof ts.toDate === 'function') return ts.toDate().getTime();
	if (ts.seconds) return ts.seconds * 1000;
	if (ts._seconds) return ts._seconds * 1000;
	return 0;
}

// Newest first — the order `fetchInquiryLeads` already returns and the order
// this page has always opened in. Sorting is a way of LOOKING at the queue
// right now, not a setting, so it is per-session and every load starts here.
export const DEFAULT_INQUIRY_SORT = { key: 'submitted', direction: 'desc' };

// Funnel order, NOT alphabetical. Sorting Status alphabetically puts Archived
// first and New fourth, which is the opposite of how the queue gets worked.
// `reviewed` is legacy (older leads only) and sits next to its modern
// equivalent; anything unknown sinks past both.
const STATUS_RANK = {
	new: 0,
	contacted: 1,
	trial: 2,
	offered: 3,
	converted: 4,
	reviewed: 5,
	archived: 6,
};

// The value each column sorts ON, which is not always what the cell renders.
// Referral renders "Friend referral · The Smiths" via referralText(), which
// returns an em-dash when empty — sorting on that would make "no referral" a
// VALUE that clumps under "—" instead of an absence that sinks. So the raw
// fields are joined here instead.
const SORT_VALUE = {
	// Contact-kind leads have no students at all, so this is blank for them and
	// they sink to the bottom in both directions — correct, since "General
	// contact" is an absence of a student rather than a name.
	student: (l) => (l.students || [])[0]?.name,
	parent: (l) => l.parent?.name,
	contact: (l) => l.parent?.email,
	interest: (l) => interestList((l.students || [])[0]?.lessonInterest).join(', '),
	location: (l) => (l.students || [])[0]?.locationPreference,
	referral: (l) => [l.referralSource, l.referredBy].filter(Boolean).join(' '),
	status: (l) => STATUS_RANK[l.status || 'new'] ?? 99,
	// `undefined` rather than 0 for a missing date, so an undated lead sinks
	// with the other blanks instead of sorting as the epoch (i.e. as the oldest
	// inquiry the studio ever received).
	submitted: (l) => tsToMs(l.createdAt) || undefined,
};

export const isSortable = (key) => Object.prototype.hasOwnProperty.call(SORT_VALUE, key);

const isBlank = (v) => v === null || v === undefined || v === '';

const compare = (a, b) =>
	typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b));

/**
 * Sort a copy of `leads`. Never mutates — the caller's array is the filter
 * result and the counts read the unfiltered list independently.
 *
 * Blanks always sink to the bottom, in BOTH directions. A missing email or an
 * unstated location is an absence, not a value: flipping to descending should
 * reverse the rows that have data, not float the empty ones to the top.
 *
 * Ties break newest-first regardless of direction, so equal values (a screen
 * full of "New", say) stay in the order the page has always shown them.
 */
export function sortInquiries(leads, sort = DEFAULT_INQUIRY_SORT) {
	const getValue = SORT_VALUE[sort?.key];
	const dir = sort?.direction === 'desc' ? -1 : 1;

	return [...leads].sort((a, b) => {
		if (getValue) {
			const av = getValue(a);
			const bv = getValue(b);
			const aBlank = isBlank(av);
			const bBlank = isBlank(bv);
			if (aBlank || bBlank) {
				if (!(aBlank && bBlank)) return aBlank ? 1 : -1;
			} else {
				const c = compare(av, bv);
				if (c !== 0) return c * dir;
			}
		}
		return tsToMs(b.createdAt) - tsToMs(a.createdAt);
	});
}

/**
 * Next sort state for a header click: same column flips direction, a new column
 * starts ascending.
 */
export function nextSort(current, key) {
	return {
		key,
		direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc',
	};
}

// Column key → header label, for the mobile sort control. The table's own
// headers carry these strings too; keeping one list means the phone control and
// the desktop headers can never drift into naming the same column differently.
export const SORT_COLUMNS = [
	{ key: 'student', label: 'Student(s)' },
	{ key: 'parent', label: 'Parent' },
	{ key: 'contact', label: 'Contact' },
	{ key: 'interest', label: 'Interest' },
	{ key: 'location', label: 'Location' },
	{ key: 'referral', label: 'Referral' },
	{ key: 'status', label: 'Status' },
	{ key: 'submitted', label: 'Submitted' },
];
